import { DEFAULT_BRANDING, normalizeBranding, applyBranding } from './apply';

const BRANDING_KEY = 'mu.branding';

function loadCached() {
  try {
    const raw = localStorage.getItem(BRANDING_KEY);
    return raw ? normalizeBranding(JSON.parse(raw)) : { ...DEFAULT_BRANDING };
  } catch (_e) {
    return { ...DEFAULT_BRANDING };
  }
}

function saveCached(branding) {
  // No cacheamos tenant_missing: un subdominio inválido visitado una vez no debe
  // "contaminar" visitas legítimas posteriores. Guardamos el branding con el flag
  // forzado a false.
  try {
    const { tenant_missing, ...rest } = branding;
    localStorage.setItem(BRANDING_KEY, JSON.stringify({ ...rest, tenant_missing: false }));
  } catch (_e) {}
}

/**
 * Vuex 3 module factory for white-label (PLS) branding. Fetches GET /api/branding
 * (public, tenant resolved by subdomain) and exposes it to the auth screens.
 *
 *    import { createBrandingModule } from '@shared/branding/store';
 *    store.registerModule('branding', createBrandingModule(http));
 */
export function createBrandingModule(http) {
  return {
    namespaced: true,
    state: () => ({
      branding: loadCached(),
      loaded: false,
    }),
    getters: {
      branding: (s) => s.branding,
      logoUrl: (s) => s.branding.logo_url,
      orgName: (s) => s.branding.name,
      organizationId: (s) => s.branding.organization_id,
      termsUrl: (s) => s.branding.terms_url,
      privacyUrl: (s) => s.branding.privacy_url,
      isWhiteLabel: (s) => !!s.branding.white_label,
      // Multitenant PLS: present when the host resolves to a dedicated org
      // subdomain. The SPA uses it to lock the UI to that tenant.
      tenant: (s) => s.branding.tenant || null,
      // True cuando el host trae un subdominio real que no existe en BD.
      tenantMissing: (s) => !!s.branding.tenant_missing,
    },
    mutations: {
      SET_BRANDING(state, branding) {
        // normalizeBranding descarta campos fuera del esquema visual, así que
        // preservamos explícitamente tenant_missing y subdomain del payload.
        const normalized = normalizeBranding(branding);
        normalized.tenant_missing = !!(branding && branding.tenant_missing);
        if (branding && branding.subdomain) normalized.subdomain = branding.subdomain;
        state.branding = normalized;
        state.loaded = true;
        saveCached(state.branding);
      },
    },
    actions: {
      // Fetch and apply the tenant branding. Falls back to the cached/default
      // MeasureUp branding on any error so the auth screens never break.
      async fetchBranding({ commit, state }) {
        try {
          const { data } = await http.get('/api/branding');
          commit('SET_BRANDING', data);
        } catch (_e) {
          commit('SET_BRANDING', state.branding);
        }
        applyBranding(state.branding);
        return state.branding;
      },
    },
  };
}
